import { Grid3x3, Mic, MicOff, Pause, PhoneForwarded, PhoneOff, Play } from 'lucide-react';

import { Badge, BadgeDot } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { IActiveCall } from '../../state/voice-ui.store';
import { callStatusMeta } from '../../utils/call-status-meta';
import { CallerIdentity } from './caller-identity';
import { CallTimer } from './call-timer';

interface InCallPanelProps {
    readonly call: IActiveCall;
    readonly onToggleMute: () => void;
    readonly onToggleHold: () => void;
    readonly onOpenKeypad: () => void;
    readonly onOpenTransfer: () => void;
    readonly onEnd: () => void;
    readonly isHoldPending: boolean;
    readonly isEndPending: boolean;
}

/** Active-call control surface: timer, mute/hold/transfer/keypad/end. */
export function InCallPanel({
    call,
    onToggleMute,
    onToggleHold,
    onOpenKeypad,
    onOpenTransfer,
    onEnd,
    isHoldPending,
    isEndPending,
}: InCallPanelProps) {
    const meta = callStatusMeta(call.status);
    const number = call.direction === 'OUTBOUND' ? call.toNumber : call.fromNumber;
    const isConnected = call.status === 'IN_PROGRESS' || call.status === 'ON_HOLD';

    return (
        // `flex-1` so a resized widget distributes the extra height here rather
        // than leaving the panel at its natural size above a band of dead space.
        <div className="flex min-h-0 flex-1 flex-col gap-4 p-[calc(1rem*var(--voice-scale,1))]">
            <div className="flex items-center justify-between">
                <Badge variant="outline" className="gap-1.5">
                    <BadgeDot style={{ backgroundColor: meta.dotColor }} />
                    {meta.label}
                </Badge>
                {/* Nothing here until the far end actually picks up: the badge
                    on the left already says "Ringing…"/"Connecting…", and a
                    duration (or a REC dot) during ringback misreports a call
                    that has not started yet. Both appear together on connect,
                    with the timer at 00:00. */}
                {call.answeredAt !== null && (
                    <div className="flex items-center gap-2 text-sm text-text-tertiary tabular-nums">
                        {call.recordingEnabled && (
                            <span
                                className="flex items-center gap-1 text-error-500"
                                title="Recording"
                            >
                                <span className="size-2 rounded-full bg-error-500" />
                                REC
                            </span>
                        )}
                        <CallTimer
                            key={call.callId}
                            answeredAt={call.answeredAt}
                            className="text-base font-semibold tracking-tight text-text-primary tabular-nums"
                        />
                    </div>
                )}
            </div>

            <CallerIdentity name={call.contact?.name ?? null} number={number} size="lg" />

            <div className="grid grid-cols-4 gap-2">
                <Button
                    type="button"
                    variant={call.muted ? 'default' : 'secondary'}
                    size="icon"
                    className="h-[calc(2.375rem*var(--voice-scale,1))] w-full rounded-2xl"
                    disabled={!isConnected}
                    onClick={onToggleMute}
                    aria-label={call.muted ? 'Unmute' : 'Mute'}
                >
                    {call.muted ? <MicOff className="size-4" /> : <Mic className="size-4" />}
                </Button>
                <Button
                    type="button"
                    variant={call.onHold ? 'default' : 'secondary'}
                    size="icon"
                    className="h-[calc(2.375rem*var(--voice-scale,1))] w-full rounded-2xl"
                    disabled={!isConnected || isHoldPending}
                    onClick={onToggleHold}
                    aria-label={call.onHold ? 'Resume' : 'Hold'}
                >
                    {call.onHold ? <Play className="size-4" /> : <Pause className="size-4" />}
                </Button>
                <Button
                    type="button"
                    variant="secondary"
                    size="icon"
                    className="h-[calc(2.375rem*var(--voice-scale,1))] w-full rounded-2xl"
                    disabled={!isConnected}
                    onClick={onOpenKeypad}
                    aria-label="Keypad"
                >
                    <Grid3x3 className="size-4" />
                </Button>
                <Button
                    type="button"
                    variant="secondary"
                    size="icon"
                    className="h-[calc(2.375rem*var(--voice-scale,1))] w-full rounded-2xl"
                    disabled={!isConnected}
                    onClick={onOpenTransfer}
                    aria-label="Transfer"
                >
                    <PhoneForwarded className="size-4" />
                </Button>
            </div>

            <Button
                type="button"
                variant="destructive"
                className={cn(
                    // `mt-auto` sends the extra height of a resized widget to
                    // the gap above End call rather than below it.
                    'mt-auto h-[calc(2.375rem*var(--voice-scale,1))] gap-2 rounded-full shadow-[0_8px_20px_-6px_rgb(239_68_68/0.5)] transition-all motion-safe:active:scale-[0.98] dark:shadow-[0_8px_20px_-6px_rgb(239_68_68/0.4)]',
                    isEndPending && 'opacity-70',
                )}
                disabled={isEndPending}
                onClick={onEnd}
            >
                <PhoneOff className="size-4" />
                End call
            </Button>
        </div>
    );
}
